import { useState, useRef, useCallback, useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Mark } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  AtSign,
  Bold,
  Code,
  File as FileIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Plus,
  Strikethrough,
  Type,
  Underline as UnderlineIcon,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FloatingOverlay } from '@/components/ui/floating-overlay';
import { useFileUpload, type UploadProgress } from '@/lib/hooks/useFileUpload';
import { api, type ChatAttachmentRef, type ChatChannelMemberResponse } from '@/lib/api';
import { AttachmentMenu, type AttachmentCategory } from './AttachmentMenu';
import { MentionOverlay } from './MentionOverlay';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReplyTarget {
  messageId: string;
  senderName: string;
  content: string;
}

interface EditTarget {
  messageId: string;
  content: string;
}

/** A file that has finished uploading and whose download URL has been resolved. */
interface CompletedAttachment {
  fileId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  downloadUrl: string;
}

export interface MessageInputProps {
  onSend: (content: string, replyToId?: string, attachmentRefs?: ChatAttachmentRef[]) => void;
  onSaveEdit?: (messageId: string, content: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onTyping?: () => void;
  /** @deprecated The attachment menu is now handled internally. */
  onAttachmentTrigger?: () => void;
  /** Members available for @ mention. */
  members?: ChatChannelMemberResponse[];
  disabled?: boolean;
  /** True when the current user has left this channel (shows read-only banner). */
  leftChannel?: boolean;
  placeholder?: string;
  replyTo?: ReplyTarget | null;
  onCancelReply?: () => void;
  editingMessage?: EditTarget | null;
  onCancelEdit?: () => void;
}

// ---------------------------------------------------------------------------
// MentionMark — custom TipTap mark that stores @mention spans in the schema.
// Using a Mark (rather than raw HTML injection) means insertContent can use
// ProseMirror JSON format, which never gets escaped as text.
// ---------------------------------------------------------------------------

const MentionMark = Mark.create({
  name: 'mention',
  spanning: false,
  addAttributes() {
    return {
      userId: { default: null },
      displayName: { default: null },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span[data-mention]',
        getAttrs: (el: string | HTMLElement) => {
          if (typeof el === 'string') return {};
          return {
            userId: el.getAttribute('data-user-id'),
            displayName: el.getAttribute('data-display-name'),
          };
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return [
      'span',
      {
        'data-mention': 'true',
        'data-user-id': HTMLAttributes['userId'],
        'data-display-name': HTMLAttributes['displayName'],
        class: 'mention-tag',
      },
      0,
    ];
  },
});

// ---------------------------------------------------------------------------
// HTML Sanitizer
// Strips all tags not in the allowlist and removes unsafe attributes/URLs.
// TipTap's schema already constrains output, but this is a defence-in-depth
// safety net before content leaves the component.
// ---------------------------------------------------------------------------

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'strike',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'a',
  'blockquote',
  'span',
]);

const ALLOWED_ATTRS: Record<string, string[]> = {
  a: ['href', 'target', 'rel'],
  span: ['data-mention', 'data-user-id', 'data-display-name', 'class'],
};

function _sanitizeNode(node: Node): void {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.COMMENT_NODE) {
      node.removeChild(child);
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap — promote children in place of the element
      const frag = document.createDocumentFragment();
      while (el.firstChild) frag.appendChild(el.firstChild);
      node.replaceChild(frag, el);
      continue;
    }

    // Strip disallowed attributes
    const allowed = ALLOWED_ATTRS[tag] ?? [];
    Array.from(el.attributes).forEach((attr) => {
      if (!allowed.includes(attr.name)) el.removeAttribute(attr.name);
    });

    // Block javascript: and data: URIs
    const href = el.getAttribute('href');
    if (href && /^(javascript:|data:)/i.test(href.trim())) {
      el.removeAttribute('href');
    }

    _sanitizeNode(el);
  }
}

function sanitizeHtml(dirty: string): string {
  const tmpl = document.createElement('template');
  tmpl.innerHTML = dirty;
  _sanitizeNode(tmpl.content);
  const wrap = document.createElement('div');
  wrap.appendChild(tmpl.content.cloneNode(true));
  return wrap.innerHTML;
}

// ---------------------------------------------------------------------------
// Editor CSS
// Scoped under .chat-composer to avoid leaking into the rest of the app.
// ---------------------------------------------------------------------------

const EDITOR_STYLES = `
.chat-composer .ProseMirror {
  outline: none;
  min-height: 40px;
  max-height: 150px;
  overflow-y: auto;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}
.chat-composer .ProseMirror > * + * { margin-top: 4px; }
.chat-composer .ProseMirror p { margin: 0; }
.chat-composer .ProseMirror ul,
.chat-composer .ProseMirror ol { padding-left: 1.4rem; margin: 4px 0; }
.chat-composer .ProseMirror li { margin: 2px 0; }
.chat-composer .ProseMirror code {
  background: rgba(0,0,0,0.07);
  border-radius: 3px;
  padding: 0.1em 0.3em;
  font-size: 0.85em;
  font-family: ui-monospace, monospace;
}
.dark .chat-composer .ProseMirror code { background: rgba(255,255,255,0.1); }
.chat-composer .ProseMirror pre {
  background: rgba(0,0,0,0.06);
  border-radius: 6px;
  padding: 8px 12px;
  overflow-x: auto;
  font-family: ui-monospace, monospace;
  font-size: 0.875em;
  margin: 4px 0;
}
.dark .chat-composer .ProseMirror pre { background: rgba(255,255,255,0.08); }
.chat-composer .ProseMirror a {
  color: hsl(var(--primary));
  text-decoration: underline;
  cursor: pointer;
}
.chat-composer .ProseMirror blockquote {
  border-left: 3px solid hsl(var(--border));
  padding-left: 0.75rem;
  color: hsl(var(--muted-foreground));
  margin: 4px 0;
}
/* Placeholder — shown on the first empty paragraph */
.chat-composer .ProseMirror p.is-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: hsl(var(--muted-foreground) / 0.6);
  pointer-events: none;
  height: 0;
}
/* Mention chips inside the composer */
.chat-composer .ProseMirror .mention-tag {
  color: #5865f2;
  font-weight: 500;
  background: rgba(88, 101, 242, 0.1);
  border-radius: 3px;
  padding: 0 2px;
}
`;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Toolbar button. Uses onMouseDown with e.preventDefault() to keep editor
 * focus and selection intact while clicking formatting controls.
 */
function ToolbarBtn({
  active,
  onClick,
  onMouseDown,
  title,
  children,
  ref,
}: {
  active?: boolean;
  onClick?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  title: string;
  children: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseDown={onMouseDown ?? ((e) => e.preventDefault())}
      title={title}
      className={cn(
        'h-7 w-7 flex items-center justify-center rounded transition-colors',
        'text-muted-foreground hover:bg-muted hover:text-foreground',
        active && 'bg-primary/10 text-primary',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-4 bg-border/60 mx-0.5 flex-shrink-0" />;
}

const BottomIconBtn = ({
  title,
  onClick,
  active,
  children,
  ref,
}: {
  title: string;
  onClick?: () => void;
  active?: boolean;
  children: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}) => (
  <button
    ref={ref}
    type="button"
    title={title}
    onClick={onClick}
    className={cn(
      'h-8 w-8 flex items-center justify-center rounded-full transition-colors',
      'text-muted-foreground hover:bg-muted hover:text-foreground',
      active && 'text-primary',
    )}
  >
    {children}
  </button>
);

// Chip shown while a file is still uploading or in a failed state
function UploadChip({
  upload,
  onCancel,
}: {
  upload: UploadProgress;
  onCancel: (fileId: string) => void;
}) {
  const isFailed = upload.status === 'failed';
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg border text-[12px] max-w-[180px]',
        isFailed
          ? 'border-destructive/40 bg-destructive/10 text-destructive'
          : 'border-border bg-muted/60 text-foreground',
      )}
    >
      <FileIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
      <span className="truncate flex-1">{upload.fileName}</span>
      {!isFailed && <span className="text-muted-foreground tabular-nums">{upload.progress}%</span>}
      <button
        type="button"
        onClick={() => onCancel(upload.fileId)}
        title={isFailed ? 'Dismiss' : 'Cancel upload'}
        className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// Chip shown for a fully uploaded attachment waiting to be sent
function AttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: CompletedAttachment;
  onRemove: (fileId: string) => void;
}) {
  const isImage = attachment.mimeType.startsWith('image/');
  return (
    <div className="flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg border border-border bg-muted/60 text-[12px] max-w-[180px]">
      {isImage ? (
        <img
          src={attachment.downloadUrl}
          alt={attachment.fileName}
          className="h-5 w-5 object-cover rounded flex-shrink-0"
        />
      ) : (
        <FileIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
      )}
      <span className="truncate flex-1 text-foreground">{attachment.fileName}</span>
      <button
        type="button"
        onClick={() => onRemove(attachment.fileId)}
        title="Remove attachment"
        className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MessageInput({
  onSend,
  onSaveEdit,
  onDeleteMessage,
  onTyping,
  onAttachmentTrigger: _onAttachmentTrigger, // deprecated; attachment menu is now internal
  members = [],
  disabled,
  leftChannel,
  placeholder,
  replyTo,
  onCancelReply,
  editingMessage,
  onCancelEdit,
}: MessageInputProps) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [hasLinkSelection, setHasLinkSelection] = useState(false);
  const [showToolbar, setShowToolbar] = useState(true);
  const [attachOpen, setAttachOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [completedAttachments, setCompletedAttachments] = useState<CompletedAttachment[]>([]);

  const isEditing = !!editingMessage;

  // Ref for the "+" button — used to anchor the attachment menu overlay.
  const plusBtnRef = useRef<HTMLButtonElement>(null);
  // Ref for the link toolbar button — used to anchor the link overlay.
  const linkBtnRef = useRef<HTMLButtonElement>(null);
  // Ref for the "@" button — used to anchor the mention overlay.
  const mentionBtnRef = useRef<HTMLButtonElement>(null);

  // Stable refs so the editor's key-handler always sees current values
  // without needing to be re-created when state changes.
  const sendHandlerRef = useRef<() => void>(() => {});
  const cancelEditRef = useRef<(() => void) | undefined>(undefined);
  const cancelReplyRef = useRef<(() => void) | undefined>(undefined);
  const editingMessageRef = useRef(editingMessage);
  const replyToRef = useRef(replyTo);
  const placeholderRef = useRef('');
  // Saved before the link Popover opens so we can restore it after
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);
  // Holds the live editor instance so stable callbacks (key handlers) can call editor commands
  const editorRef = useRef<Editor | null>(null);

  useEffect(() => {
    editingMessageRef.current = editingMessage;
  }, [editingMessage]);
  useEffect(() => {
    replyToRef.current = replyTo;
  }, [replyTo]);
  useEffect(() => {
    cancelEditRef.current = onCancelEdit;
  }, [onCancelEdit]);
  useEffect(() => {
    cancelReplyRef.current = onCancelReply;
  }, [onCancelReply]);
  useEffect(() => {
    placeholderRef.current = isEditing
      ? 'Edit message… (clear to delete)'
      : (placeholder ?? 'Type a message…');
  }, [isEditing, placeholder]);

  // ----- File upload -----
  const { uploads, uploadFiles, cancelUpload } = useFileUpload({
    onUploadComplete: async (file) => {
      try {
        const { downloadUrl } = await api.getDownloadUrl(file.fileId);
        setCompletedAttachments((prev) => [
          ...prev,
          {
            fileId: file.fileId,
            fileName: file.fileName,
            mimeType: file.mimeType ?? '',
            sizeBytes: file.sizeBytes,
            downloadUrl,
          },
        ]);
      } catch {
        // download URL resolution failing shouldn't block the composer
      }
    },
  });

  const handleFilesSelected = useCallback(
    (files: File[], category: AttachmentCategory) => {
      uploadFiles(files, { category });
    },
    [uploadFiles],
  );

  const removeCompletedAttachment = useCallback((fileId: string) => {
    setCompletedAttachments((prev) => prev.filter((a) => a.fileId !== fileId));
  }, []);

  // Active uploads that are still in-flight (not yet resolved to a CompletedAttachment)
  const activeUploads = uploads.filter(
    (u) => u.status === 'uploading' || u.status === 'processing' || u.status === 'failed',
  );

  // ----- TipTap editor -----
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable nodes that don't belong in a chat composer
        heading: false,
        horizontalRule: false,
        blockquote: false,
      }),
      Underline,
      MentionMark,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        // Function form so we always read the latest placeholder string
        // from the ref without needing to recreate the editor.
        placeholder: () => placeholderRef.current,
      }),
    ],
    editorProps: {
      handleKeyDown: (view, event) => {
        // Enter (without Shift) → send/save, unless the cursor is inside a list node.
        // Inside a list, TipTap must handle Enter itself (new item / exit list).
        if (event.key === 'Enter' && !event.shiftKey) {
          const $from = view.state.selection.$from;
          let inList = false;
          for (let d = $from.depth; d >= 0; d--) {
            if ($from.node(d).type.name === 'listItem') {
              inList = true;
              break;
            }
          }
          if (inList) return false; // delegate to TipTap's list key handler
          event.preventDefault();
          sendHandlerRef.current();
          return true;
        }
        // Shift+Backspace inside a list → lift list item back to a plain paragraph
        if (event.key === 'Backspace' && event.shiftKey) {
          const $from = view.state.selection.$from;
          let inList = false;
          for (let d = $from.depth; d >= 0; d--) {
            if ($from.node(d).type.name === 'listItem') {
              inList = true;
              break;
            }
          }
          if (inList) {
            event.preventDefault();
            editorRef.current?.chain().liftListItem('listItem').run();
            return true;
          }
        }
        // Shift+Enter → hard break (handled natively by StarterKit)
        // Escape → cancel edit or reply
        if (event.key === 'Escape') {
          if (editingMessageRef.current) cancelEditRef.current?.();
          else if (replyToRef.current) cancelReplyRef.current?.();
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        if (!event.clipboardData) return false;
        const text = event.clipboardData.getData('text/plain').trim();
        // Paste a plain URL onto selected text → wrap selection in a link
        if (!/^https?:\/\//i.test(text)) return false;
        if (view.state.selection.empty) return false;
        editorRef.current?.chain().focus().setLink({ href: text }).run();
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      if (!e.isEmpty) onTyping?.();
    },
  });

  // ----- Send / save handler -----
  const handleSend = useCallback(() => {
    if (!editor || disabled) return;
    // Block if any file is still uploading — the user must wait for uploads to finish.
    if (uploads.some((u) => u.status === 'uploading' || u.status === 'processing')) return;

    if (editingMessage) {
      if (!editor.isEmpty) {
        const html = sanitizeHtml(editor.getHTML());
        onSaveEdit?.(editingMessage.messageId, html);
      } else {
        onDeleteMessage?.(editingMessage.messageId);
        onCancelEdit?.();
      }
      editor.commands.clearContent();
      return;
    }

    const hasText = !editor.isEmpty;
    const hasAttachments = completedAttachments.length > 0;
    if (!hasText && !hasAttachments) return;

    const html = hasText ? sanitizeHtml(editor.getHTML()) : '';

    // Build structured attachment refs to pass alongside the message content.
    const attachmentRefs: ChatAttachmentRef[] = completedAttachments.map((a) => ({
      fileId: a.fileId,
      fileName: a.fileName,
      mimeType: a.mimeType,
      sizeBytes: a.sizeBytes,
    }));

    onSend(html, replyTo?.messageId, attachmentRefs.length > 0 ? attachmentRefs : undefined);
    editor.commands.clearContent();
    editor.commands.focus();
    setCompletedAttachments([]);
    onCancelReply?.();
  }, [
    editor,
    disabled,
    uploads,
    editingMessage,
    replyTo,
    completedAttachments,
    onSend,
    onSaveEdit,
    onDeleteMessage,
    onCancelEdit,
    onCancelReply,
  ]);

  // Keep the ref current so the editor's key-handler always calls the latest version
  useEffect(() => {
    sendHandlerRef.current = handleSend;
  }, [handleSend]);
  // Keep editorRef in sync so stable key/paste handlers can issue editor commands
  useEffect(() => {
    if (editor) editorRef.current = editor;
  }, [editor]);

  // ----- Sync edit mode into editor -----
  useEffect(() => {
    if (!editor) return;
    if (editingMessage) {
      // false = don't fire onUpdate (avoid spurious typing indicators)
      editor.commands.setContent(editingMessage.content);
      editor.commands.focus('end');
    } else {
      editor.commands.clearContent();
    }
  }, [editor, editingMessage]);

  // ----- Focus when a reply is initiated -----
  useEffect(() => {
    if (replyTo && editor) editor.commands.focus('end');
  }, [editor, replyTo]);

  // ----- Link insertion -----
  // We save the ProseMirror selection in onMouseDown (before the editor loses
  // focus to the Popover's URL input), then restore it when applying the link.
  const handleLinkMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); // keeps editor focused & selection intact
    if (!editor) return;
    if (linkOpen) {
      setLinkOpen(false);
      return;
    }
    const { from, to } = editor.state.selection;
    savedSelectionRef.current = { from, to };
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    setHasLinkSelection(from !== to);
    setLinkText(selectedText);
    setLinkUrl(editor.getAttributes('link').href ?? '');
    setLinkOpen(true);
  };

  const handleInsertLink = useCallback(() => {
    if (!editor || !linkUrl.trim()) return;
    const url =
      linkUrl.startsWith('http://') || linkUrl.startsWith('https://')
        ? linkUrl
        : `https://${linkUrl}`;

    const saved = savedSelectionRef.current;

    if (saved && saved.from !== saved.to) {
      // Text was selected — apply link mark to that selection
      editor
        .chain()
        .focus()
        .setTextSelection({ from: saved.from, to: saved.to })
        .setLink({ href: url })
        .run();
    } else {
      // No selection — insert display text (or URL) with a link mark at cursor
      const text = linkText.trim() || url;
      const insertAt = saved ? saved.from : editor.state.selection.from;
      editor
        .chain()
        .focus()
        .insertContentAt(insertAt, {
          type: 'text',
          text,
          marks: [
            { type: 'link', attrs: { href: url, target: '_blank', rel: 'noopener noreferrer' } },
          ],
        })
        .run();
    }

    setLinkUrl('');
    setLinkText('');
    setLinkOpen(false);
  }, [editor, linkUrl, linkText]);

  const handleRemoveLink = useCallback(() => {
    editor?.chain().focus().unsetLink().run();
    setLinkOpen(false);
  }, [editor]);

  const handleLinkKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInsertLink();
    }
    if (e.key === 'Escape') setLinkOpen(false);
  };

  const isEmpty = (editor?.isEmpty ?? true) && completedAttachments.length === 0;
  const isUploading = uploads.some((u) => u.status === 'uploading' || u.status === 'processing');

  if (leftChannel) {
    return (
      <div className="border-t border-border bg-card px-4 py-3 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">You left this channel</p>
      </div>
    );
  }

  return (
    <div className="border-t border-border bg-card px-4 pt-2 pb-3 chat-composer">
      {/* Scoped editor styles */}
      <style dangerouslySetInnerHTML={{ __html: EDITOR_STYLES }} />

      {/* Reply banner */}
      {replyTo && !isEditing && (
        <div className="flex items-start gap-2 mb-2 pl-2 pr-1 py-1.5 bg-muted/40 rounded-lg border-l-[3px] border-indigo-400">
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-indigo-600 leading-tight">
              {replyTo.senderName}
            </p>
            <p className="text-[12px] text-muted-foreground truncate">{replyTo.content}</p>
          </div>
          <button
            type="button"
            onClick={onCancelReply}
            className="flex-shrink-0 p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors"
            title="Cancel reply"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Edit banner */}
      {isEditing && (
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="text-[12px] font-medium text-amber-600">Editing message</p>
          <button
            type="button"
            onClick={onCancelEdit}
            className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Composer box ── */}
      <div
        className={cn(
          'rounded-xl border border-border bg-background overflow-hidden transition-opacity',
          disabled && 'opacity-50 pointer-events-none',
        )}
      >
        {/* ── Formatting toolbar ── */}
        {showToolbar && (
          <div className="flex items-center gap-0.5 px-2 pt-1.5 pb-1 border-b border-border/40 flex-wrap">
            <ToolbarBtn
              active={editor?.isActive('bold')}
              onClick={() => editor?.chain().focus().toggleBold().run()}
              title="Bold (Ctrl+B)"
            >
              <Bold className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn
              active={editor?.isActive('italic')}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              title="Italic (Ctrl+I)"
            >
              <Italic className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn
              active={editor?.isActive('underline')}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              title="Underline (Ctrl+U)"
            >
              <UnderlineIcon className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn
              active={editor?.isActive('strike')}
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              title="Strikethrough"
            >
              <Strikethrough className="h-3.5 w-3.5" />
            </ToolbarBtn>

            <Divider />

            {/* Link — saves selection on mousedown, then opens FloatingOverlay */}
            <ToolbarBtn
              ref={linkBtnRef}
              active={editor?.isActive('link') || linkOpen}
              onMouseDown={handleLinkMouseDown}
              title="Insert link"
            >
              <Link2 className="h-3.5 w-3.5" />
            </ToolbarBtn>

            <Divider />

            <ToolbarBtn
              active={editor?.isActive('bulletList')}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              title="Bullet list"
            >
              <List className="h-3.5 w-3.5" />
            </ToolbarBtn>
            <ToolbarBtn
              active={editor?.isActive('orderedList')}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              title="Numbered list"
            >
              <ListOrdered className="h-3.5 w-3.5" />
            </ToolbarBtn>

            <Divider />

            <ToolbarBtn
              active={editor?.isActive('code')}
              onClick={() => editor?.chain().focus().toggleCode().run()}
              title="Inline code"
            >
              <Code className="h-3.5 w-3.5" />
            </ToolbarBtn>
          </div>
        )}

        {/* ── Rich text editor ── */}
        <EditorContent editor={editor} />

        {/* ── Attachment previews — shown between editor and action bar ── */}
        {(activeUploads.length > 0 || completedAttachments.length > 0) && (
          <div className="flex flex-wrap gap-2 px-3 py-2 border-t border-border/40">
            {/* In-flight uploads */}
            {activeUploads.map((u) => (
              <UploadChip key={u.fileId} upload={u} onCancel={cancelUpload} />
            ))}
            {/* Completed attachments waiting to be sent */}
            {completedAttachments.map((a) => (
              <AttachmentChip key={a.fileId} attachment={a} onRemove={removeCompletedAttachment} />
            ))}
          </div>
        )}

        {/* ── Bottom action bar ── */}
        <div className="flex items-center justify-between px-2 py-1.5 border-t border-border/40">
          {/* Left: utility actions */}
          <div className="flex items-center gap-0.5">
            {/* Attachment — opens viewport-aware floating menu */}
            <BottomIconBtn
              ref={plusBtnRef}
              title="Attach file"
              active={attachOpen}
              onClick={() => setAttachOpen((v) => !v)}
            >
              <Plus className="h-4 w-4" />
            </BottomIconBtn>

            {/* Formatting toolbar toggle */}
            <BottomIconBtn
              title={showToolbar ? 'Hide formatting' : 'Show formatting'}
              onClick={() => setShowToolbar((v) => !v)}
              active={showToolbar}
            >
              <Type className="h-4 w-4" />
            </BottomIconBtn>

            {/* Emoji — trigger only; picker is a future ticket */}
            {/* <BottomIconBtn title="Emoji (coming soon)">
              <Smile className="h-4 w-4" />
            </BottomIconBtn> */}

            {/* Mention — opens member picker overlay */}
            <BottomIconBtn
              ref={mentionBtnRef}
              title="Mention someone"
              active={mentionOpen}
              onClick={() => setMentionOpen((v) => !v)}
            >
              <AtSign className="h-4 w-4" />
            </BottomIconBtn>
          </div>

          {/* Right: send / save */}
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || (isEmpty && !isEditing) || isUploading}
            className="px-4 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={isUploading ? 'Waiting for uploads to complete…' : undefined}
          >
            {isEditing ? 'Save' : isUploading ? 'Uploading…' : 'Send'}
          </button>
        </div>
      </div>

      {/* Attachment menu — rendered in a portal, anchored to the "+" button */}
      <AttachmentMenu
        anchorRef={plusBtnRef}
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        onFilesSelected={handleFilesSelected}
      />

      {/* Link overlay — anchored to the link toolbar button */}
      <FloatingOverlay
        open={linkOpen}
        onClose={() => {
          setLinkOpen(false);
          setLinkUrl('');
          setLinkText('');
        }}
        anchorRef={linkBtnRef}
        preferredPlacement="top"
        className="w-80 rounded-xl border border-border bg-card shadow-lg p-3"
      >
        <p className="text-xs font-semibold text-foreground mb-2.5">Insert link</p>

        {/* Display-text field — only shown when there is no text selection */}
        {!hasLinkSelection && (
          <div className="mb-2">
            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">
              Display text
            </label>
            <input
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              onKeyDown={handleLinkKeyDown}
              placeholder="Link label"
              className="w-full text-sm px-2.5 py-1.5 border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/50 bg-background"
            />
          </div>
        )}

        <div className="mb-2.5">
          <label className="text-[11px] font-medium text-muted-foreground mb-1 block">URL</label>
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={handleLinkKeyDown}
            placeholder="https://example.com"
            className="w-full text-sm px-2.5 py-1.5 border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/50 bg-background"
            autoFocus
          />
        </div>

        <div className="flex items-center justify-between">
          {editor?.isActive('link') && (
            <button
              type="button"
              onClick={handleRemoveLink}
              className="text-xs text-destructive hover:underline"
            >
              Remove link
            </button>
          )}
          <button
            type="button"
            onClick={handleInsertLink}
            disabled={!linkUrl.trim()}
            className="ml-auto text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </FloatingOverlay>

      {/* Mention overlay — anchored to the "@" button */}
      <MentionOverlay
        open={mentionOpen}
        onClose={() => setMentionOpen(false)}
        anchorRef={mentionBtnRef}
        members={members}
        onSelect={(member) => {
          const displayName = member.displayName || member.nickname || member.userId;
          editor
            ?.chain()
            .focus()
            .insertContent([
              {
                type: 'text',
                text: `@${displayName}`,
                marks: [{ type: 'mention', attrs: { userId: member.userId, displayName } }],
              },
              { type: 'text', text: ' ' },
            ])
            .run();
          setMentionOpen(false);
        }}
      />
    </div>
  );
}
